import { Dialog, DialogTitle, DialogContent, TextField, DialogActions, Button, Alert } from "@mui/material";
import { AuthContext } from "components/authProvider/authProvider";
import CustomAlert from "components/generals/alert/CustomAlert";
import { host } from "config";
import React, { useState, useEffect, useContext, FormEvent, ChangeEvent } from "react";

type UserData = {
  id: string;
  data: any;
};

type RateAccessProps = {
  userData: UserData;
  open: boolean;
  onClose: () => void;
  setAlertOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setAlertMessage: React.Dispatch<React.SetStateAction<string>>;
};

interface Payload {
  uid: string;
  email: string;
  firstName: string;
  lastName: string;
  currentRole: string;
  brokerageId: string;
  crmId: string;
}

const AddRaterAccess: React.FC<RateAccessProps> = ({ userData, open, onClose, setAlertMessage, setAlertOpen }) => {
  const [payload, setPayload] = useState<Payload>({
    uid: userData.id,
    email: userData.data?.Email,
    firstName: userData.data?.First_name,
    lastName: userData.data?.Last_name,
    currentRole: userData.data?.Role,
    brokerageId: "",
    crmId: "",
  });
  const [openErrorAlert, setOpenErrorAlert] = useState<boolean>(false);
  const [errorAlertMessage, setErrorAlertMessage] = useState<string>("");

  const authContext = useContext(AuthContext); // Get Auth context
  if (!authContext) {
    throw new Error("AuthContext is undefined");
  }
  const { currentUser } = authContext;

  function handleFormChange(event: React.ChangeEvent<HTMLInputElement>) {
    setPayload({ ...payload, [event.target.name]: event.target.value });
  }

  function checkRequiredFields() {
    const requiredFields = {
      email: "email",
      currentRole: "Users Current Role",
    };

    const conditionalRequiredFields = {
      crmId: "CRM Contact ID",
      brokerageId: "CRM Brokerage ID ",
    };

    const emptyFields = (fieldsToCheck) => {
      return Object.keys(fieldsToCheck)
        .filter((field) => !payload[field] || (payload[field] && payload[field]?.toString().trim() === ""))
        .map((key) => fieldsToCheck[key]);
    };
    let allFields =
      payload.currentRole != "admin" && payload.currentRole != "internal_ugp"
        ? emptyFields(requiredFields).concat(emptyFields(conditionalRequiredFields))
        : emptyFields(requiredFields);
    if (allFields.length > 0) {
      throw new Error(`Missing required fields: ${allFields.join(", ")}.`);
    }
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      checkRequiredFields();
      currentUser
        ?.getIdToken(false)
        .then((idToken: string) => {
          return idToken;
        })
        .then((tk) => {
          fetch(`${host}/api/firebase/api/ratingAccess`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              authorization: `Bearer ${tk}`,
            },
            body: JSON.stringify(payload),
          }).then((response: Response) =>
            response.json().then((resp) => {
              setAlertMessage(resp.data ?? resp.error);
              onClose();
              setAlertOpen(true);
            })
          );
        })
        .catch((e) => {
          setAlertMessage(e.message);
          onClose();
          setAlertOpen(true);
        });
    } catch (error) {
      setErrorAlertMessage(error.message);
      setOpenErrorAlert(true);
    }
  };

  return (
    <Dialog open={true} onClose={onClose} aria-labelledby="dialog-title" maxWidth="md" fullWidth>
      <DialogTitle id="dialog-title">Add Rating Access</DialogTitle>
      <DialogContent>
        {openErrorAlert && (
          <Alert sx={{ marginBottom: "1rem" }} severity="error" onClose={() => setOpenErrorAlert(false)}>
            {errorAlertMessage}
          </Alert>
        )}
        <form onSubmit={handleSubmit}>
          <TextField
            autoFocus
            margin="dense"
            name="email"
            label="Email Address"
            type="email"
            fullWidth
            value={payload.email}
            onChange={handleFormChange}
          />
          <TextField
            autoFocus
            margin="dense"
            name="currentRole"
            label="Users Current Role"
            type="text"
            fullWidth
            value={payload.currentRole}
          />
          {payload.currentRole != "admin" && payload.currentRole != "internal_ugp" && (
            <React.Fragment>
              <TextField
                autoFocus
                margin="dense"
                name="brokerageId"
                label="CRM Brokerage ID"
                type="text"
                fullWidth
                value={payload.brokerageId}
                onChange={handleFormChange}
              />
              <TextField
                autoFocus
                margin="dense"
                name="crmId"
                label="CRM Contact ID"
                type="text"
                fullWidth
                value={payload.crmId}
                onChange={handleFormChange}
              />
            </React.Fragment>
          )}
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="submit">Submit</Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddRaterAccess;
